import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { FnolRequest, PetBreedDTO, PetInsuranceData, UserDto, VetChatSummaryDto } from "../models";
import { calculatePaidAmount, findPrevContract } from "../utils";

@Injectable({
  providedIn: 'root'
})
export class DataStorageService {
  private _getAllData: PetInsuranceData[] = [];
  private $selectedData: BehaviorSubject<PetInsuranceData | undefined> = new BehaviorSubject<PetInsuranceData | undefined>(undefined);
  private $loaded = new BehaviorSubject(false);
  private $user = new BehaviorSubject<UserDto | undefined>(undefined);
  private $fnolRequests = new BehaviorSubject<FnolRequest[]>([]);
  private $chatSummaries = new BehaviorSubject<VetChatSummaryDto[]>([]);
  private $catBreeds = new BehaviorSubject<PetBreedDTO[]>([]);
  private $dogBreeds = new BehaviorSubject<PetBreedDTO[]>([]);
  private $petPictureUpdated = new Subject<string>();

  constructor() {
  }

  get selectedData(): PetInsuranceData | undefined {
    return this.$selectedData.value;
  }

  set selectedData(value: PetInsuranceData) {
    this.$selectedData.next(value);
  }

  get selectedData$(): Observable<PetInsuranceData | undefined> {
    return this.$selectedData.asObservable();
  }

  get getAllData(): PetInsuranceData[] {
    return this._getAllData;
  }

  set getAllData(value: PetInsuranceData[]) {
    value.forEach(item => {
      const prevContract = findPrevContract(value, item);
      const calculation = calculatePaidAmount(item, prevContract);
      item.paidAmount = calculation.paidAmount;
      item.usedFranchise = calculation.usedFranchise;
      item.contractStatus = calculation.contractStatus;
    })

    const statusPriority: Record<string, number> = {
      'active': 0,
      'future': 1,
      'expired': 2
    };

    this._getAllData = value.sort((a, b) =>
      statusPriority[a.contractStatus] - statusPriority[b.contractStatus]
    );
  }

  get loaded$(): Observable<boolean> {
    return this.$loaded.asObservable();
  }

  get loaded(): boolean {
    return this.$loaded.value;
  }

  set loaded(loaded: boolean) {
    this.$loaded.next(loaded);
  }

  get user(): UserDto | undefined {
    return this.$user.value;
  }

  get user$(): Observable<UserDto | undefined> {
    return this.$user.asObservable();
  }

  set user(user: UserDto | undefined) {
    this.$user.next(user);
  }

  get fnolRequests(): FnolRequest[] {
    return this.$fnolRequests.value;
  }

  set fnolRequests(fnolRequests: FnolRequest[]) {
    this.$fnolRequests.next(fnolRequests);
  }

  get fnolRequests$(): Observable<FnolRequest[]> {
    return this.$fnolRequests.asObservable();
  }

  get chatSummaries(): VetChatSummaryDto[] {
    return this.$chatSummaries.value;
  }

  set chatSummaries(chatSummaries: VetChatSummaryDto[]) {
    this.$chatSummaries.next(chatSummaries);
  }

  get chatSummaries$(): Observable<VetChatSummaryDto[]> {
    return this.$chatSummaries.asObservable();
  }

  set catBreeds(catBreeds: PetBreedDTO[]) {
    this.$catBreeds.next(catBreeds);
  }

  get catBreeds$(): Observable<PetBreedDTO[]> {
    return this.$catBreeds.asObservable();
  }

  get catBreeds(): PetBreedDTO[] {
    return this.$catBreeds.value;
  }

  set dogBreeds(dogBreeds: PetBreedDTO[]) {
    this.$dogBreeds.next(dogBreeds);
  }

  get dogBreeds$(): Observable<PetBreedDTO[]> {
    return this.$dogBreeds.asObservable();
  }

  get dogBreeds(): PetBreedDTO[] {
    return this.$dogBreeds.value;
  }

  get petPictureUpdated$(): Observable<string> {
    return this.$petPictureUpdated.asObservable();
  }

  notifyPetPictureUpdated(baseNumber: string): void {
    this.$petPictureUpdated.next(baseNumber);
  }

  logout() {
    this._getAllData = [];
    this.$selectedData.next(undefined);
    this.$loaded.next(false);
    this.$user.next(undefined);
    this.$fnolRequests.next([]);
    this.$chatSummaries.next([]);
  }
}
